import { useState, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Bot, RefreshCw, X, Filter, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, MessageCircle, Rocket, Link as LinkIcon, Pause, Play, Trash2, Users, Copy, Pencil, UserCog } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { agentService, Agent, teamService } from '../services/api'
import type { TeamInstance } from '../types/team'

// * Side panel component for agent details
function AgentDetailsPanel({ agent, isOpen, onClose }: { agent: Agent | null; isOpen: boolean; onClose: () => void }) {
  const panelHeight = 'calc(100vh - 200px)'
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showChangeTeamModal, setShowChangeTeamModal] = useState(false)
  const [selectedTeamInstanceId, setSelectedTeamInstanceId] = useState<string>('')
  const [selectedRoleId, setSelectedRoleId] = useState<string>('')
  
  // * Fetch team data
  const { data: teamTemplates } = useQuery('teamTemplates', teamService.getAllTeamTemplates)
  const { data: teamInstances } = useQuery('teamInstances', teamService.getAllTeamInstances)
  
  // * Initialize team/role from agent config
  useEffect(() => {
    if (agent && showChangeTeamModal) {
      const config = agent.config as any
      setSelectedTeamInstanceId(config?.teamInstanceId || '')
      setSelectedRoleId(config?.roleId || '')
    }
  }, [agent, showChangeTeamModal])
  
  // * Get selected team instance and template
  const selectedTeamInstance = teamInstances?.find(ti => ti.id === selectedTeamInstanceId)
  const selectedTeamTemplate = selectedTeamInstance
    ? teamTemplates?.find(t => t.id === selectedTeamInstance.teamTemplateId)
    : null
  
  // * Get available roles for selected team instance
  const availableRoles = selectedTeamInstance && selectedTeamTemplate
    ? selectedTeamTemplate.roles.filter(role => {
        // * Check if role is already occupied (if unique)
        if (role.isUnique) {
          const occupied = selectedTeamInstance.agents?.some(a => a.roleId === role.id && a.agentId !== agent?.id)
          return !occupied
        }
        return true
      })
    : []
  
  // * Mutation to change team assignment
  const changeTeamMutation = useMutation(
    async ({ agentId, teamInstanceId, roleId }: { agentId: string; teamInstanceId?: string; roleId?: string }) => {
      // * Remove from current team if assigned
      const currentTeamInstanceId = (agent?.config as any)?.teamInstanceId
      if (currentTeamInstanceId) {
        await teamService.removeAgentFromTeam(agentId, currentTeamInstanceId)
      }
      
      // * Assign to new team if provided
      if (teamInstanceId && roleId) {
        await teamService.assignAgentToTeam(agentId, teamInstanceId, roleId)
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('activeAgents')
        queryClient.invalidateQueries('teamInstances')
        setShowChangeTeamModal(false)
      },
      onError: (error: any) => {
        alert(`Erreur lors du changement d'équipe: ${error.message || 'Erreur inconnue'}`)
      }
    }
  )
  
  const handleChangeTeam = async () => {
    if (!agent) return
    
    // * Validation: if team is selected, role is required
    if (selectedTeamInstanceId && !selectedRoleId) {
      alert('Veuillez sélectionner un rôle dans l\'équipe')
      return
    }
    
    await changeTeamMutation.mutateAsync({
      agentId: agent.id,
      teamInstanceId: selectedTeamInstanceId || undefined,
      roleId: selectedRoleId || undefined
    })
  }
  
  if (!isOpen) return null
  
  // * Mutations for agent management
  const hibernateMutation = useMutation(agentService.hibernateAgent, {
    onSuccess: async () => {
      await queryClient.invalidateQueries('activeAgents')
      // * Force refetch to update UI immediately
      const { data: updatedAgents } = await queryClient.refetchQueries('activeAgents')
      // * Update selectedAgent with fresh data
      if (selectedAgent && updatedAgents) {
        const updatedAgent = updatedAgents.find((a: Agent) => a.id === selectedAgent.id)
        if (updatedAgent) {
          setSelectedAgent(updatedAgent)
        }
      }
    },
  })

  const resumeMutation = useMutation(agentService.resumeAgent, {
    onSuccess: async () => {
      await queryClient.invalidateQueries('activeAgents')
      // * Force refetch to update UI immediately
      const { data: updatedAgents } = await queryClient.refetchQueries('activeAgents')
      // * Update selectedAgent with fresh data
      if (selectedAgent && updatedAgents) {
        const updatedAgent = updatedAgents.find((a: Agent) => a.id === selectedAgent.id)
        if (updatedAgent) {
          setSelectedAgent(updatedAgent)
        }
      }
    },
  })

  const deleteMutation = useMutation(agentService.deleteAgentInstance, {
    onSuccess: () => {
      queryClient.invalidateQueries('activeAgents')
      // * Close panel if agent was deleted
      onClose()
    },
  })

  // * State for chat interface status
  const [chatInterfaceEnabled, setChatInterfaceEnabled] = useState<boolean | null>(null)
  const [isCheckingChatStatus, setIsCheckingChatStatus] = useState(false)

  const formatLastHeartbeat = (heartbeat: string | null | undefined): string => {
    if (!heartbeat) return 'Jamais'
    
    const date = new Date(heartbeat)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'À l\'instant'
    if (diffMins < 60) return `Il y a ${diffMins} min`
    if (diffHours < 24) return `Il y a ${diffHours}h`
    return `Il y a ${diffDays}j`
  }

  const formatRegistrationDate = (date: string | null | undefined): string => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDateTime = (date: string | null | undefined): string => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const getStatusColor = (status: string | null | undefined): string => {
    switch (status) {
      case 'active':
        return 'status-dot-success'
      case 'dormant':
        return 'status-dot-warning'
      case 'inactive':
        return 'status-dot-inactive'
      default:
        return 'status-dot-inactive'
    }
  }

  const getStatusLabel = (status: string | null | undefined): string => {
    switch (status) {
      case 'active':
        return 'Actif'
      case 'dormant':
        return 'Dormant'
      case 'inactive':
        return 'Inactif'
      default:
        return 'Inconnu'
    }
  }

  const getChatUrl = (agent: Agent | null): string => {
    if (!agent) return '#'
    
    // * Construct chat URL via Traefik routing
    // * Use containerUuid instead of agent.id to ensure consistency
    // * The agent.id may change when the agent registers, but containerUuid is stable
    // * Pattern: https://core.atf.therapia.solutions/agents/CONTAINER_UUID/chat
    const coreBaseUrl = import.meta.env.VITE_CORE_API_URL?.replace('/api', '') || 'https://core.atf.therapia.solutions'
    const baseUrl = coreBaseUrl.replace(/\/$/, '')
    
    // * Get containerUuid from agent config (stable, unique per container)
    // * Fallback to agent.id for backward compatibility with old containers
    const containerUuid = (agent.config as any)?.containerUuid
    const identifier = containerUuid || agent.id || agent.name || 'unknown'
    
    // * Sanitize identifier (same logic as AgentDeploymentService)
    // * Convert to lowercase, replace spaces and special chars with hyphens
    const sanitizedId = identifier
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
    
    console.log('getChatUrl:', { 
      agentId: agent.id, 
      containerUuid: containerUuid,
      agentName: agent.name, 
      sanitizedId, 
      url: `${baseUrl}/agents/${sanitizedId}/chat` 
    })
    
    return `${baseUrl}/agents/${sanitizedId}/chat`
  }

  // * Check chat interface status
  const checkChatStatus = async () => {
    if (!agent?.name) return
    
    setIsCheckingChatStatus(true)
    try {
      // * Construct URL via Traefik routing
      // * Pattern: https://core.atf.therapia.solutions/agents/AGENT_NAME/chat/api/chat-interface/status
      const chatUrl = getChatUrl(agent)
      const statusUrl = `${chatUrl}/api/chat-interface/status`
      
      const response = await fetch(statusUrl)
      if (response.ok) {
        const data = await response.json()
        setChatInterfaceEnabled(data.chat_interface_enabled ?? true)
      } else {
        setChatInterfaceEnabled(false)
      }
    } catch (error) {
      console.error('Error checking chat status:', error)
      setChatInterfaceEnabled(false)
    } finally {
      setIsCheckingChatStatus(false)
    }
  }

  // * Enable chat interface
  const enableChatInterface = async () => {
    if (!agent?.name) return
    
    try {
      // * Construct URL via Traefik routing
      // * Pattern: https://core.atf.therapia.solutions/agents/AGENT_NAME/chat/api/chat-interface/toggle
      const chatUrl = getChatUrl(agent)
      const toggleUrl = `${chatUrl}/api/chat-interface/toggle`
      
      const response = await fetch(toggleUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: true })
      })
      if (response.ok) {
        setChatInterfaceEnabled(true)
      }
    } catch (error) {
      console.error('Error enabling chat:', error)
      alert('Erreur lors de l\'activation du chat')
    }
  }

  // * Handle chat button click
  const handleChatClick = async (e: React.MouseEvent) => {
    if (!agent?.agentUrl) return
    
    // * Check status if not already checked
    if (chatInterfaceEnabled === null) {
      await checkChatStatus()
      return
    }

    // * If chat is disabled, show dialog to enable it
    if (chatInterfaceEnabled === false) {
      e.preventDefault()
      if (window.confirm('L\'interface de chat est désactivée. Voulez-vous l\'activer ?')) {
        await enableChatInterface()
        // * Open chat after enabling
        window.open(getChatUrl(agent), '_blank')
      }
      return
    }

    // * Chat is enabled, let the link work normally
  }

  // * Check chat status when agent changes
  useEffect(() => {
    if (agent?.agentUrl) {
      checkChatStatus()
    } else {
      setChatInterfaceEnabled(null)
    }
  }, [agent?.id, agent?.agentUrl])

  // * Handle hibernate/resume
  const handleHibernateResume = async () => {
    if (!agent) return
    
    if (agent.activeStatus === 'inactive') {
      // * Resume
      await resumeMutation.mutateAsync(agent.id)
    } else {
      // * Hibernate
      await hibernateMutation.mutateAsync(agent.id)
    }
  }

  // * Handle delete
  const handleDelete = async () => {
    if (!agent) return
    
    if (window.confirm(
      'Êtes-vous sûr de vouloir supprimer cette instance d\'agent ?\n\n' +
      'Cette action est irréversible et supprimera :\n' +
      '- Le conteneur Docker\n' +
      '- L\'enregistrement de l\'agent dans la base de données\n\n' +
      'L\'agent continuera d\'exister en tant que modèle, mais cette instance sera définitivement supprimée.'
    )) {
      await deleteMutation.mutateAsync(agent.id)
    }
  }

  if (!agent) {
    return (
      <div className="w-80 border-l border-gray-200 bg-gray-50 flex items-center justify-center" style={{ height: panelHeight }}>
        <p className="text-sm text-secondary">Aucun agent sélectionné</p>
      </div>
    )
  }

  return (
    <div className="w-80 border-l border-gray-200 bg-white flex flex-col" style={{ height: panelHeight }}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-bold text-primary">Détails de l'agent</h2>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Fermer"
          title="Fermer le panneau"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {/* Agent info block */}
        <div className="flex items-center space-x-4 pb-4 border-b border-gray-200">
          <Bot className="w-8 h-8 icon-light flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-2">
              <p className="text-lg font-semibold text-primary">{agent.name}</p>
              <div className={`w-3 h-3 rounded-full ${getStatusColor(agent.activeStatus)}`}></div>
              <span className="text-sm text-secondary">
                {getStatusLabel(agent.activeStatus)}
              </span>
            </div>
            <p className="text-sm text-secondary">
              {agent.description || 'Aucune description'}
            </p>
            {agent.agentUrl && (
              <p className="text-xs text-tertiary mt-1 truncate">
                {agent.agentUrl}
              </p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2 pb-4 border-b border-gray-200">
          {/* Pause/Play button */}
          <button
            onClick={handleHibernateResume}
            disabled={hibernateMutation.isLoading || resumeMutation.isLoading}
            className={`p-2 rounded-lg transition-colors ${
              agent.activeStatus === 'inactive'
                ? 'bg-green-100 hover:bg-green-200 text-green-700'
                : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={agent.activeStatus === 'inactive' ? 'Redémarrer l\'agent (play)' : 'Hiberner l\'agent (pause)'}
          >
            {agent.activeStatus === 'inactive' ? (
              <Play className="w-5 h-5" />
            ) : (
              <Pause className="w-5 h-5" />
            )}
          </button>

          {/* Chat button */}
          {agent.agentUrl && (
            <a
              href={getChatUrl(agent)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleChatClick}
              className={`p-2 rounded-lg transition-colors ${
                chatInterfaceEnabled === false || isCheckingChatStatus
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
              }`}
              title={
                isCheckingChatStatus
                  ? 'Vérification du statut du chat...'
                  : chatInterfaceEnabled === false
                  ? 'Interface de chat désactivée (cliquez pour activer)'
                  : 'Ouvrir l\'interface de chat'
              }
            >
              <MessageCircle className="w-5 h-5" />
            </a>
          )}

          {/* Deploy button */}
          <button
            onClick={() => navigate(`/agents/deploy?agentId=${agent.id}`)}
            className="p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors"
            title="Déployer une nouvelle instance"
          >
            <Rocket className="w-5 h-5" />
          </button>

          {/* Change team/role button */}
          <button
            onClick={() => setShowChangeTeamModal(true)}
            className="p-2 rounded-lg bg-indigo-100 hover:bg-indigo-200 text-indigo-700 transition-colors"
            title="Changer l'équipe et le rôle"
          >
            <UserCog className="w-5 h-5" />
          </button>

          {/* Delete button */}
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isLoading}
            className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Supprimer l'instance (irréversible)"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
        
        {/* Current team/role info */}
        {(agent.config as any)?.teamInstanceName && (
          <div className="pb-4 border-b border-gray-200">
            <p className="text-xs font-semibold text-tertiary mb-1">Équipe</p>
            <p className="text-sm text-primary">
              {(agent.config as any)?.teamInstanceName || '—'}
            </p>
            {(agent.config as any)?.roleName && (
              <>
                <p className="text-xs font-semibold text-tertiary mb-1 mt-2">Rôle</p>
                <p className="text-sm text-primary">
                  {(agent.config as any)?.roleName || '—'}
                </p>
              </>
            )}
          </div>
        )}

        {/* Details grid */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <p className="text-xs font-semibold text-tertiary mb-1">Dernier heartbeat</p>
            <p className="text-sm text-primary">{formatLastHeartbeat(agent.lastHeartbeat)}</p>
          </div>
          {agent.registeredAt && (
            <div>
              <p className="text-xs font-semibold text-tertiary mb-1">Inscrit le</p>
              <p className="text-sm text-primary">{formatRegistrationDate(agent.registeredAt)}</p>
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-tertiary mb-1">Version</p>
            <p className="text-sm text-primary">{agent.version || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-tertiary mb-1">Créé le</p>
            <p className="text-sm text-primary">{formatDateTime(agent.createdAt)}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-tertiary mb-1">Dernière utilisation</p>
            <p className="text-sm text-primary">{formatDateTime(agent.lastHeartbeat || agent.registeredAt)}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-tertiary mb-1">ID</p>
            <p className="text-xs text-tertiary font-mono truncate">{agent.id}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-tertiary mb-1">UUID</p>
            <p className="text-xs text-tertiary font-mono truncate">
              {(agent.config as any)?.containerUuid || 'N/A'}
            </p>
          </div>
          {(agent.config as any)?.engineName && (
            <div>
              <p className="text-xs font-semibold text-tertiary mb-1">Moteur</p>
              <p className="text-sm text-primary">
                {(agent.config as any)?.engineName || 'N/A'}
                {(agent.config as any)?.engineVersion && (
                  <span className="text-xs text-secondary ml-2">
                    (v{(agent.config as any)?.engineVersion})
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Change team/role modal */}
      {showChangeTeamModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Changer l'équipe et le rôle</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Équipe :</label>
                <select
                  value={selectedTeamInstanceId}
                  onChange={(e) => {
                    setSelectedTeamInstanceId(e.target.value)
                    setSelectedRoleId('') // * Reset role when team changes
                  }}
                  className="form-input w-full"
                >
                  <option value="">— Aucune équipe —</option>
                  {teamInstances?.map(ti => (
                    <option key={ti.id} value={ti.id}>
                      {ti.teamTemplateName} {ti.name ? `- ${ti.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedTeamInstanceId && (
                <div>
                  <label className="block text-sm font-medium mb-2">Rôle :</label>
                  <select
                    value={selectedRoleId}
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                    className="form-input w-full"
                  >
                    <option value="">— Sélectionner un rôle —</option>
                    {availableRoles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name} {role.isUnique && '(Unique)'}
                      </option>
                    ))}
                  </select>
                  {availableRoles.length === 0 && (
                    <p className="text-xs text-secondary mt-1">
                      Aucun rôle disponible. Tous les rôles sont peut-être déjà occupés.
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowChangeTeamModal(false)}
                className="button button--secondary"
                disabled={changeTeamMutation.isLoading}
              >
                Annuler
              </button>
              <button
                onClick={handleChangeTeam}
                className="button button--primary"
                disabled={changeTeamMutation.isLoading || (selectedTeamInstanceId && !selectedRoleId)}
              >
                {changeTeamMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

type SortDirection = 'asc' | 'desc' | null
type ColumnKey = 'name' | 'status' | 'createdAt' | 'lastUsed' | 'description' | 'uuid' | 'agentId' | 'owner' | 'team' | 'role'

interface ColumnSort {
  column: ColumnKey | null
  direction: SortDirection
}

interface ColumnFilter {
  column: ColumnKey
  value: string
}

export function Monitoring() {
  const navigate = useNavigate()
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [isPanelOpen, setIsPanelOpen] = useState(true)
  const [sort, setSort] = useState<ColumnSort>({ column: null, direction: null })
  const [filters, setFilters] = useState<ColumnFilter[]>([])
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set())
  const [timeRange, setTimeRange] = useState<string>('1d')
  const [filterDropdowns, setFilterDropdowns] = useState<Record<ColumnKey, boolean>>({
    name: false,
    status: false,
    createdAt: false,
    lastUsed: false,
    description: false,
    uuid: false,
    agentId: false,
    owner: false,
    team: false,
    role: false
  })
  
  const queryClient = useQueryClient()
  
  // * Fetch active agents with auto-refresh every 30 seconds
  const { data: activeAgents, isLoading, refetch } = useQuery(
    'activeAgents',
    agentService.getActiveAgents,
    {
      refetchInterval: 30000, // * Refresh every 30 seconds
      onSuccess: () => {
        setLastRefresh(new Date())
      }
    }
  )

  // * Fetch team instances
  const { data: teamInstances } = useQuery('teamInstances', teamService.getAllTeamInstances)



  // * Calculate metrics from real data
  const totalActiveAgents = activeAgents?.length || 0
  const activeCount = activeAgents?.filter(agent => agent.activeStatus === 'active').length || 0
  const dormantCount = activeAgents?.filter(agent => agent.activeStatus === 'dormant').length || 0
  const inactiveCount = activeAgents?.filter(agent => agent.activeStatus === 'inactive').length || 0

  // * Get unique values for each column for filters
  const getUniqueStatusValues = useMemo(() => {
    if (!activeAgents) return []
    const statuses = new Set(activeAgents.map(a => a.activeStatus).filter(Boolean))
    return Array.from(statuses).sort()
  }, [activeAgents])

  // * Apply filters and sorting
  const filteredAndSortedAgents = useMemo(() => {
    if (!activeAgents) return []

    // * Apply filters in column order (left to right)
    let filtered = [...activeAgents]
    
    // * Filter by name
    const nameFilter = filters.find(f => f.column === 'name')
    if (nameFilter && nameFilter.value) {
      filtered = filtered.filter(agent => 
        agent.name.toLowerCase().includes(nameFilter.value.toLowerCase())
      )
    }

    // * Filter by description
    const descriptionFilter = filters.find(f => f.column === 'description')
    if (descriptionFilter && descriptionFilter.value) {
      filtered = filtered.filter(agent => 
        (agent.description || '').toLowerCase().includes(descriptionFilter.value.toLowerCase())
      )
    }

    // * Filter by status
    const statusFilter = filters.find(f => f.column === 'status')
    if (statusFilter && statusFilter.value) {
      filtered = filtered.filter(agent => agent.activeStatus === statusFilter.value)
    }

    // * Filter by createdAt (date range or exact match)
    const createdAtFilter = filters.find(f => f.column === 'createdAt')
    if (createdAtFilter && createdAtFilter.value) {
      const filterDate = new Date(createdAtFilter.value)
      filtered = filtered.filter(agent => {
        const agentDate = new Date(agent.registeredAt || agent.createdAt)
        return agentDate.toDateString() === filterDate.toDateString()
      })
    }

    // * Filter by lastUsed (date range or exact match)
    const lastUsedFilter = filters.find(f => f.column === 'lastUsed')
    if (lastUsedFilter && lastUsedFilter.value) {
      const filterDate = new Date(lastUsedFilter.value)
      filtered = filtered.filter(agent => {
        const agentDate = new Date(agent.lastHeartbeat || agent.registeredAt || agent.createdAt)
        return agentDate.toDateString() === filterDate.toDateString()
      })
    }

    // * Filter by UUID
    const uuidFilter = filters.find(f => f.column === 'uuid')
    if (uuidFilter && uuidFilter.value) {
      filtered = filtered.filter(agent => {
        const uuid = (agent.config as any)?.containerUuid || agent.id || ''
        return uuid.toLowerCase().includes(uuidFilter.value.toLowerCase())
      })
    }

        // * Filter by owner (instance manager, not creator)
        const ownerFilter = filters.find(f => f.column === 'owner')
        if (ownerFilter && ownerFilter.value) {
          filtered = filtered.filter(agent => {
            const owner = agent.ownerUsername || 'N/A' // * Only instance manager, not creator
            return owner.toLowerCase().includes(ownerFilter.value.toLowerCase())
          })
        }

    // * Filter by team
    const teamFilter = filters.find(f => f.column === 'team')
    if (teamFilter && teamFilter.value) {
      filtered = filtered.filter(agent => {
        const teamName = (agent.config as any)?.teamInstanceName || '—'
        return teamName.toLowerCase().includes(teamFilter.value.toLowerCase())
      })
    }

    // * Filter by role
    const roleFilter = filters.find(f => f.column === 'role')
    if (roleFilter && roleFilter.value) {
      filtered = filtered.filter(agent => {
        const roleName = (agent.config as any)?.roleName || '—'
        return roleName.toLowerCase().includes(roleFilter.value.toLowerCase())
      })
    }

    // * Apply sorting after filtering
    if (sort.column && sort.direction) {
      filtered.sort((a, b) => {
        let aValue: any
        let bValue: any

        switch (sort.column) {
          case 'name':
            aValue = a.name.toLowerCase()
            bValue = b.name.toLowerCase()
            break
          case 'status':
            aValue = a.activeStatus || ''
            bValue = b.activeStatus || ''
            break
          case 'createdAt':
            aValue = new Date(a.registeredAt || a.createdAt).getTime()
            bValue = new Date(b.registeredAt || b.createdAt).getTime()
            break
          case 'lastUsed':
            aValue = new Date(a.lastHeartbeat || a.registeredAt || a.createdAt).getTime()
            bValue = new Date(b.lastHeartbeat || b.registeredAt || b.createdAt).getTime()
            break
          case 'description':
            aValue = (a.description || '').toLowerCase()
            bValue = (b.description || '').toLowerCase()
            break
          case 'uuid':
            aValue = ((a.config as any)?.containerUuid || '').toLowerCase()
            bValue = ((b.config as any)?.containerUuid || '').toLowerCase()
            break
          case 'agentId':
            aValue = a.id.toLowerCase()
            bValue = b.id.toLowerCase()
            break
          case 'owner':
            aValue = (a.ownerUsername || a.creatorUsername || 'N/A').toLowerCase()
            bValue = (b.ownerUsername || b.creatorUsername || 'N/A').toLowerCase()
            break
          case 'team':
            aValue = ((a.config as any)?.teamInstanceName || '—').toLowerCase()
            bValue = ((b.config as any)?.teamInstanceName || '—').toLowerCase()
            break
          case 'role':
            aValue = ((a.config as any)?.roleName || '—').toLowerCase()
            bValue = ((b.config as any)?.roleName || '—').toLowerCase()
            break
          default:
            return 0
        }

        if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1
        if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1
        return 0
      })
    }

    return filtered
  }, [activeAgents, filters, sort])

  // * Format date for table
  const formatDate = (date: string | null | undefined): string => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  // * Format date and time for table
  const formatDateTime = (date: string | null | undefined): string => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // * Get status badge
  const getStatusBadge = (status: string | null | undefined) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold badge-success">Actif</span>
      case 'dormant':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold badge-warning">Dormant</span>
      case 'inactive':
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold badge-inactive">Inactif</span>
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold badge-inactive">Inconnu</span>
    }
  }

  // * Format UUID (first 5 characters + xxxx...)
  const formatUuid = (agent: Agent): string => {
    const uuid = (agent.config as any)?.containerUuid || ''
    if (!uuid) return 'N/A'
    if (uuid.length <= 5) return uuid
    return `${uuid.substring(0, 5)}xxxx...`
  }

  // * Format Agent ID (registration UUID)
  const formatAgentId = (agent: Agent): string => {
    if (!agent.id) return 'N/A'
    if (agent.id.length <= 5) return agent.id
    return `${agent.id.substring(0, 5)}xxxx...`
  }

  const handleRowClick = (agent: Agent) => {
    setSelectedAgent(agent)
    setIsPanelOpen(true)
  }



  const handleSort = (column: ColumnKey) => {
    if (sort.column === column) {
      // * Cycle: asc -> desc -> null
      if (sort.direction === 'asc') {
        setSort({ column, direction: 'desc' })
      } else if (sort.direction === 'desc') {
        setSort({ column: null, direction: null })
      }
    } else {
      setSort({ column, direction: 'asc' })
    }
  }

  const handleFilterChange = (column: ColumnKey, value: string) => {
    setFilters(prev => {
      const filtered = prev.filter(f => f.column !== column)
      if (value) {
        return [...filtered, { column, value }]
      }
      return filtered
    })
    setFilterDropdowns(prev => ({ ...prev, [column]: false }))
  }

  const toggleFilterDropdown = (column: ColumnKey) => {
    setFilterDropdowns(prev => ({
      ...prev,
      [column]: !prev[column]
    }))
  }

  const getFilterValue = (column: ColumnKey): string => {
    return filters.find(f => f.column === column)?.value || ''
  }

  const isFilterActive = (column: ColumnKey): boolean => {
    return filters.some(f => f.column === column && f.value)
  }

  const getSortIcon = (column: ColumnKey) => {
    if (sort.column === column) {
      if (sort.direction === 'asc') {
        return <ChevronUp className="w-2 h-2 inline-block ml-1" />
      }
      if (sort.direction === 'desc') {
        return <ChevronDown className="w-2 h-2 inline-block ml-1" />
      }
    }
    // * Always show triangle, but with reduced opacity when not active
    return <ChevronUp className="w-2 h-2 inline-block ml-1 opacity-30" />
  }

  return (
    <div className="page-content relative min-h-screen">
      {/* Image de robot en arrière-plan */}
      <div className="background-image">
        <img src="/logo.jpg" alt="Robot background" />
      </div>
      
      {/* Contenu au premier plan */}
      <div className="content-wrapper">
    <div className="w-screen" style={{ marginLeft: 'calc(50% - 50vw)', marginRight: 'calc(50% - 50vw)', paddingLeft: '2rem', paddingRight: '2rem' }}>
      <div className="flex w-full" style={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}>
        {/* Main content */}
        <div className="flex-1 space-y-4 overflow-y-auto pr-4">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-extrabold mb-2 tracking-tight tab_title">Surveillance des Instances d'agents</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/agents/deploy')}
              className="button button--primary flex items-center space-x-2"
              title="Déployer un nouvel agent"
            >
              <Rocket className="w-4 h-4" />
              <span>Déployer un agent</span>
            </button>
            <div className="flex items-center space-x-2">
              <RefreshCw className={`w-2 h-2 text-secondary ${isLoading ? 'animate-spin' : ''}`} />
              <span className="text-sm text-secondary">
                Dernière mise à jour: {lastRefresh.toLocaleTimeString('fr-FR')}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full status-dot-success"></div>
              <span className="text-sm text-secondary">Système opérationnel</span>
            </div>
          </div>
        </div>

        {/* Métriques principales - Grille de 4 colonnes */}
        <div className="grid grid-cols-4 gap-4 items-center px-4 py-3 stat-card w-full">
              <div className="col-span-1 min-w-0">
                <span className="text-sm font-medium text-tertiary whitespace-nowrap">Actifs: <span className="text-lg font-semibold text-primary">{activeCount}</span> <span className="text-xs text-success">({Math.round((activeCount / totalActiveAgents) * 100) || 0}%)</span></span>
              </div>
              <div className="col-span-1 min-w-0">
                <span className="text-sm font-medium text-tertiary whitespace-nowrap">Dormants: <span className="text-lg font-semibold text-primary">{dormantCount}</span> <span className="text-xs text-warning">({Math.round((dormantCount / totalActiveAgents) * 100) || 0}%)</span></span>
              </div>
              <div className="col-span-1 min-w-0">
                <span className="text-sm font-medium text-tertiary whitespace-nowrap">Inactifs: <span className="text-lg font-semibold text-primary">{inactiveCount}</span> <span className="text-xs text-error">({Math.round((inactiveCount / totalActiveAgents) * 100) || 0}%)</span></span>
              </div>
              <div className="col-span-1 min-w-0">
                <span className="text-sm font-medium text-tertiary whitespace-nowrap">Total: <span className="text-lg font-semibold text-primary">{totalActiveAgents}</span></span>
              </div>
            </div>

        {/* Tableau filtrable */}
        <div className="card overflow-hidden">
          <div className="px-6 py-4 list-header flex items-center justify-between">
            <h2 className="text-lg font-medium text-primary">Agents déployés</h2>
            <button
              onClick={() => setIsPanelOpen(!isPanelOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title={isPanelOpen ? 'Masquer le panneau' : 'Afficher le panneau'}
            >
              {isPanelOpen ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </button>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-robot-blue" />
            </div>
          ) : !activeAgents || activeAgents.length === 0 ? (
            <div className="text-center py-12">
              <Bot className="w-12 h-12 mx-auto mb-4 icon-light" />
              <h3 className="text-lg font-medium mb-2 text-primary">Aucun agent actif</h3>
              <p className="text-secondary">
                Aucun agent n'est actuellement enregistré dans le système.
              </p>
            </div>
          ) : filteredAndSortedAgents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-secondary">Aucun agent ne correspond aux filtres.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header bg-gray-50">
                  <tr>
                    <th className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider select-none w-12">
                      <input
                        type="checkbox"
                        checked={selectedAgents.size > 0 && selectedAgents.size === filteredAndSortedAgents.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAgents(new Set(filteredAndSortedAgents.map(a => a.id)))
                          } else {
                            setSelectedAgents(new Set())
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="cursor-pointer"
                        title="Sélectionner tous les agents"
                      />
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Nom</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('name')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('name')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('name') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.name && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[200px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={getFilterValue('name')}
                            onChange={(e) => handleFilterChange('name', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                            autoFocus
                          />
                        </div>
                      )}
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Statut</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('status')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('status')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('status') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.status && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[150px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <select
                            value={getFilterValue('status')}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                          >
                            <option value="">Tous</option>
                            {getUniqueStatusValues.map(status => (
                              <option key={status} value={status || ''}>
                                {status === 'active' ? 'Actif' : status === 'dormant' ? 'Dormant' : 'Inactif'}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Date de création</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('createdAt')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('createdAt')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('createdAt') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.createdAt && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[200px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="date"
                            value={getFilterValue('createdAt')}
                            onChange={(e) => handleFilterChange('createdAt', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                          />
                        </div>
                      )}
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('lastUsed')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Dernière utilisation</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('lastUsed')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('lastUsed')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('lastUsed') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.lastUsed && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[200px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="date"
                            value={getFilterValue('lastUsed')}
                            onChange={(e) => handleFilterChange('lastUsed', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                          />
                        </div>
                      )}
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('description')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Description</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('description')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('description')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('description') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.description && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[200px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={getFilterValue('description')}
                            onChange={(e) => handleFilterChange('description', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                            autoFocus
                          />
                        </div>
                      )}
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('uuid')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Container UUID</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('uuid')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('uuid')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('uuid') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.uuid && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[200px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={getFilterValue('uuid')}
                            onChange={(e) => handleFilterChange('uuid', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                            autoFocus
                          />
                        </div>
                      )}
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('agentId')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Agent ID</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('agentId')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('agentId')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('agentId') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.agentId && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[200px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={getFilterValue('agentId')}
                            onChange={(e) => handleFilterChange('agentId', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                            autoFocus
                          />
                        </div>
                      )}
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('owner')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Propriétaire</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('owner')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('owner')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('owner') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.owner && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[200px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={getFilterValue('owner')}
                            onChange={(e) => handleFilterChange('owner', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                            autoFocus
                          />
                        </div>
                      )}
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('team')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Equipe</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('team')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('team')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('team') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.team && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[200px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={getFilterValue('team')}
                            onChange={(e) => handleFilterChange('team', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                            autoFocus
                          />
                        </div>
                      )}
                    </th>
                    <th 
                      className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none relative"
                      onClick={() => handleSort('role')}
                    >
                      <div className="flex items-center justify-between">
                        <span>Role</span>
                        <div className="flex items-center gap-1">
                          {getSortIcon('role')}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFilterDropdown('role')
                            }}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Filter 
                              className={`w-2 h-2 ${isFilterActive('role') ? 'text-success' : 'text-gray-600'}`}
                            />
                          </button>
                        </div>
                      </div>
                      {filterDropdowns.role && (
                        <div 
                          className="absolute mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 p-2 min-w-[200px]"
                          style={{ position: 'absolute', marginTop: '4px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            placeholder="Rechercher..."
                            value={getFilterValue('role')}
                            onChange={(e) => handleFilterChange('role', e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
                            autoFocus
                          />
                        </div>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedAgents.map((agent) => (
                    <tr
                      key={agent.id}
                      onClick={() => handleRowClick(agent)}
                      className={`table-row cursor-pointer transition-colors ${
                        selectedAgent?.id === agent.id 
                          ? 'bg-blue-50 hover:bg-blue-100' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedAgents.has(agent.id)}
                          onChange={(e) => {
                            e.stopPropagation()
                            const newSelected = new Set(selectedAgents)
                            if (e.target.checked) {
                              newSelected.add(agent.id)
                            } else {
                              newSelected.delete(agent.id)
                            }
                            setSelectedAgents(newSelected)
                          }}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-primary">{agent.name}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(agent.activeStatus)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                        {formatDate(agent.registeredAt || agent.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                        {formatDateTime(agent.lastHeartbeat || agent.registeredAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary">
                        <p className="truncate max-w-xs">{agent.description || 'Aucune description'}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary font-mono">
                        {formatUuid(agent)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary font-mono">
                        {formatAgentId(agent)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                      {(() => {
                        // * Display instance manager (owner), not creator
                        const ownerName = agent.ownerUsername || 'N/A'
                        const ownerId = agent.ownerId
                        return ownerId && ownerName !== 'N/A' 
                          ? `${ownerName} (${ownerId})`
                          : ownerName
                      })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
                        {(agent.config as any)?.teamInstanceName || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
                        {(agent.config as any)?.roleName || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Teams monitoring block */}
        <div className="card overflow-hidden">
          <div className="px-6 py-4 list-header flex items-center justify-between">
            <h2 className="text-lg font-medium text-primary flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Instances d'équipes
            </h2>
          </div>
          <div className="px-6 py-4">
            {!teamInstances || teamInstances.length === 0 ? (
              <p className="text-sm text-secondary text-center py-4">
                Aucune instance d'équipe créée
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="table-header bg-gray-50">
                    <tr>
                      <th className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider">
                        Modèle d'équipe
                      </th>
                      <th className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider">
                        Nom de l'instance
                      </th>
                      <th className="table-header-cell px-6 py-3 text-left text-xs font-semibold text-secondary uppercase tracking-wider">
                        Agents
                      </th>
                      <th className="table-header-cell px-6 py-3 text-right text-xs font-semibold text-secondary uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {teamInstances.map((teamInstance) => (
                      <TeamInstanceRow
                        key={teamInstance.id}
                        teamInstance={teamInstance}
                        activeAgents={activeAgents || []}
                        queryClient={queryClient}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Statistics block */}
        <div className="card overflow-hidden">
          <div className="px-6 py-4 list-header flex items-center justify-between">
            <h2 className="text-lg font-medium text-primary">Statistiques</h2>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-robot-blue"
            >
              <option value="1h">1 heure</option>
              <option value="10h">10 heures</option>
              <option value="1d">1 jour</option>
              <option value="3d">3 jours</option>
              <option value="10d">10 jours</option>
              <option value="1m">1 mois</option>
              <option value="1y">1 an</option>
            </select>
          </div>
          <div className="px-6 py-4">
            {selectedAgents.size === 0 ? (
              <p className="text-sm text-secondary text-center py-8">
                Sélectionnez au moins un agent pour afficher les statistiques
              </p>
            ) : (
              <AgentStatisticsChart
                agentIds={Array.from(selectedAgents)}
                timeRange={timeRange}
              />
            )}
          </div>
        </div>
        </div>

        {/* Side panel */}
        {isPanelOpen && (
          <AgentDetailsPanel
            agent={selectedAgent}
            isOpen={isPanelOpen}
            onClose={() => setIsPanelOpen(false)}
          />
        )}
      </div>
    </div>
      </div>
    </div>
  )
}

// * Team Instance Row Component
function TeamInstanceRow({ 
  teamInstance, 
  activeAgents, 
  queryClient 
}: { 
  teamInstance: TeamInstance
  activeAgents: Agent[]
  queryClient: any
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [newName, setNewName] = useState(teamInstance.name || '')
  const [reassignments, setReassignments] = useState<Record<string, { teamInstanceId?: string; roleId?: string }>>({})
  const { data: teamTemplates } = useQuery('teamTemplates', teamService.getAllTeamTemplates)
  const { data: allTeamInstances } = useQuery('teamInstances', teamService.getAllTeamInstances)
  
  const updateTeamInstanceMutation = useMutation(
    (name: string) => teamService.updateTeamInstance(teamInstance.id, { name }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('teamInstances')
        setShowRenameModal(false)
      },
      onError: (error: any) => {
        alert(`Erreur lors du renommage: ${error.message || 'Erreur inconnue'}`)
      }
    }
  )

  // * Get agents assigned to this team instance
  const assignedAgents = activeAgents.filter(agent => 
    (agent.config as any)?.teamInstanceId === teamInstance.id
  )

  const handleDelete = async () => {
    try {
      const reassignmentsArray = Object.entries(reassignments).map(([agentId, assignment]) => ({
        agentId,
        teamInstanceId: assignment.teamInstanceId,
        roleId: assignment.roleId
      }))
      await teamService.deleteTeamInstance(teamInstance.id, reassignmentsArray.length > 0 ? reassignmentsArray : undefined)
      queryClient.invalidateQueries('teamInstances')
      queryClient.invalidateQueries('activeAgents')
      setShowDeleteModal(false)
    } catch (error: any) {
      alert(`Erreur lors de la suppression: ${error.message || 'Erreur inconnue'}`)
    }
  }

  const handleDuplicate = async () => {
    try {
      await teamService.duplicateTeamInstance(teamInstance.id)
      queryClient.invalidateQueries('teamInstances')
      queryClient.invalidateQueries('activeAgents')
    } catch (error: any) {
      alert(`Erreur lors de la duplication: ${error.message || 'Erreur inconnue'}`)
    }
  }

  // * Initialize reassignments for delete modal
  useEffect(() => {
    if (showDeleteModal) {
      const initialReassignments: Record<string, { teamInstanceId?: string; roleId?: string }> = {}
      assignedAgents.forEach(agent => {
        initialReassignments[agent.id] = {}
      })
      setReassignments(initialReassignments)
    }
  }, [showDeleteModal, assignedAgents])

  // * Initialize name for rename modal
  useEffect(() => {
    if (showRenameModal) {
      setNewName(teamInstance.name || '')
    }
  }, [showRenameModal, teamInstance.name])

  const handleRename = async () => {
    await updateTeamInstanceMutation.mutateAsync(newName)
  }

  return (
    <>
      <tr className="table-row table-hover-row">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
          {teamInstance.teamTemplateName}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-primary">
          {teamInstance.name || '—'}
        </td>
        <td className="px-6 py-4 text-sm text-primary">
          {assignedAgents.length > 0 ? (
            <div className="space-y-1">
              {assignedAgents.map(agent => (
                <div key={agent.id} className="flex items-center space-x-2">
                  <Bot className="w-4 h-4 text-robot-blue" />
                  <span>{agent.name}</span>
                  <span className="text-xs text-secondary">
                    ({(agent.config as any)?.roleName || 'Sans rôle'})
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-secondary">Aucun agent</span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={() => setShowRenameModal(true)}
              className="action-button action-button--secondary"
              title="Renommer l'instance d'équipe"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={handleDuplicate}
              className="action-button action-button--primary"
              title="Dupliquer l'équipe"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="action-button action-button--danger"
              title="Supprimer l'équipe"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>

      {/* Rename modal - using portal to render outside table */}
      {showRenameModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Renommer l'instance d'équipe</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Nouveau nom :</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="form-input w-full"
                placeholder="Nom de l'instance (optionnel)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRename()
                  } else if (e.key === 'Escape') {
                    setShowRenameModal(false)
                  }
                }}
                autoFocus
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowRenameModal(false)}
                className="button button--secondary"
                disabled={updateTeamInstanceMutation.isLoading}
              >
                Annuler
              </button>
              <button
                onClick={handleRename}
                className="button button--primary"
                disabled={updateTeamInstanceMutation.isLoading}
              >
                {updateTeamInstanceMutation.isLoading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirmation modal - using portal to render outside table */}
      {showDeleteModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Supprimer l'instance d'équipe</h3>
            <p className="text-sm text-secondary mb-4">
              Êtes-vous sûr de vouloir supprimer l'instance d'équipe "{teamInstance.teamTemplateName} {teamInstance.name ? `- ${teamInstance.name}` : ''}" ?
            </p>
            
            {assignedAgents.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium mb-2">Agents actifs dans cette équipe :</p>
                <div className="space-y-3">
                  {assignedAgents.map(agent => (
                    <div key={agent.id} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Bot className="w-4 h-4 text-robot-blue" />
                          <span className="font-medium">{agent.name}</span>
                          <span className="text-xs text-secondary">
                            ({(agent.config as any)?.roleName || 'Sans rôle'})
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-medium">Réassigner à :</label>
                        <select
                          value={reassignments[agent.id]?.teamInstanceId || ''}
                          onChange={(e) => {
                            setReassignments({
                              ...reassignments,
                              [agent.id]: {
                                ...reassignments[agent.id],
                                teamInstanceId: e.target.value || undefined,
                                roleId: undefined // * Reset role when team changes
                              }
                            })
                          }}
                          className="form-input text-sm w-full"
                        >
                          <option value="">— Aucune équipe —</option>
                          {allTeamInstances?.filter(ti => ti.id !== teamInstance.id).map(ti => (
                            <option key={ti.id} value={ti.id}>
                              {ti.teamTemplateName} {ti.name ? `- ${ti.name}` : ''}
                            </option>
                          ))}
                        </select>
                        {reassignments[agent.id]?.teamInstanceId && (() => {
                          const selectedTeamInstance = allTeamInstances?.find(ti => ti.id === reassignments[agent.id]?.teamInstanceId)
                          const selectedTeamTemplate = selectedTeamInstance 
                            ? teamTemplates?.find(t => t.id === selectedTeamInstance.teamTemplateId)
                            : null
                          return (
                            <select
                              value={reassignments[agent.id]?.roleId || ''}
                              onChange={(e) => {
                                setReassignments({
                                  ...reassignments,
                                  [agent.id]: {
                                    ...reassignments[agent.id],
                                    roleId: e.target.value || undefined
                                  }
                                })
                              }}
                              className="form-input text-sm w-full"
                            >
                              <option value="">— Sélectionner un rôle —</option>
                              {selectedTeamTemplate?.roles.map(role => (
                                <option key={role.id} value={role.id}>
                                  {role.name} {role.isUnique && '(Unique)'}
                                </option>
                              ))}
                            </select>
                          )
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="button button--secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="button button--danger"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

// * Statistics chart component
function AgentStatisticsChart({ agentIds, timeRange }: { agentIds: string[]; timeRange: string }) {
  const { data: agents } = useQuery('activeAgents', agentService.getActiveAgents)

  // * Calculate time range
  const getTimeRangeDates = () => {
    const now = new Date()
    let startTime = new Date()
    
    switch (timeRange) {
      case '1h':
        startTime = new Date(now.getTime() - 60 * 60 * 1000)
        break
      case '10h':
        startTime = new Date(now.getTime() - 10 * 60 * 60 * 1000)
        break
      case '1d':
        startTime = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        break
      case '3d':
        startTime = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000)
        break
      case '10d':
        startTime = new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000)
        break
      case '1m':
        startTime = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case '1y':
        startTime = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
        break
    }
    
    return { startTime, endTime: now }
  }

  const { startTime, endTime } = getTimeRangeDates()
  
  const { data: metrics, isLoading } = useQuery(
    ['agentMetrics', agentIds.join(','), startTime.toISOString(), endTime.toISOString()],
    () => agentService.getAgentMetrics(agentIds, startTime, endTime),
    {
      refetchInterval: 60000, // * Refresh every minute
    }
  )

  // * Process metrics for chart
  const chartData = useMemo(() => {
    if (!metrics || metrics.length === 0) return []

    // * Group by timestamp and aggregate
    const grouped = new Map<string, any>()
    
    metrics.forEach((metric: any) => {
      const timestamp = new Date(metric.timestamp).toISOString()
      const key = timestamp.substring(0, 16) // * Group by minute
      
      if (!grouped.has(key)) {
        grouped.set(key, {
          time: key,
          activity: 0,
          userMessages: 0,
          userTokens: 0,
          agentMessagesToUser: 0,
          agentMessagesToAgent: 0,
          agentTokensCoT: 0,
          agentTokensToUser: 0,
          agentTokensToAgent: 0
        })
      }
      
      const entry = grouped.get(key)!
      entry.activity += metric.activity || 0
      entry.userMessages += metric.user_messages_count || 0
      entry.userTokens += metric.user_tokens_count || 0
      entry.agentMessagesToUser += metric.agent_messages_to_user_count || 0
      entry.agentMessagesToAgent += metric.agent_messages_to_agent_count || 0
      entry.agentTokensCoT += metric.agent_tokens_chain_of_thought || 0
      entry.agentTokensToUser += metric.agent_tokens_to_user || 0
      entry.agentTokensToAgent += metric.agent_tokens_to_agent || 0
    })

    return Array.from(grouped.values()).sort((a, b) => a.time.localeCompare(b.time))
  }, [metrics])

  if (isLoading) {
    return <div className="text-center py-8 text-secondary">Chargement des statistiques...</div>
  }

  if (chartData.length === 0) {
    return <div className="text-center py-8 text-secondary">Aucune donnée disponible pour la période sélectionnée</div>
  }

  return (
    <div className="space-y-4">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="time" 
            tickFormatter={(value) => new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          />
          <YAxis />
          <Tooltip 
            labelFormatter={(value) => new Date(value).toLocaleString('fr-FR')}
          />
          <Legend />
          <Line type="monotone" dataKey="activity" stroke="#8884d8" name="Activité" />
          <Line type="monotone" dataKey="userMessages" stroke="#82ca9d" name="Messages utilisateur" />
          <Line type="monotone" dataKey="userTokens" stroke="#ffc658" name="Tokens utilisateur" />
          <Line type="monotone" dataKey="agentMessagesToUser" stroke="#ff7300" name="Messages agent → utilisateur" />
          <Line type="monotone" dataKey="agentMessagesToAgent" stroke="#8dd1e1" name="Messages agent → agent" />
          <Line type="monotone" dataKey="agentTokensCoT" stroke="#d084d0" name="Tokens CoT" />
          <Line type="monotone" dataKey="agentTokensToUser" stroke="#ffb347" name="Tokens agent → utilisateur" />
          <Line type="monotone" dataKey="agentTokensToAgent" stroke="#87ceeb" name="Tokens agent → agent" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
